import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col
} from 'reactstrap';
// ⬇️ usar la versión externa con HMAC
import ExtAddressAutocomplete from 'components/extAddressAutocomplete';

const AddressModal = ({ isOpen, toggle, onSave, addressToEdit, getAvailableAddressTypes }) => {
  const [formData, setFormData] = useState({
    addressType: '',
    streetAddress: '',
    streetAddress2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA'
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (addressToEdit) {
      setFormData({
        addressType: addressToEdit.addressType || '',
        streetAddress: addressToEdit.streetAddress || '',
        streetAddress2: addressToEdit.streetAddress2 || '',
        city: addressToEdit.city || '',
        state: addressToEdit.state || '',
        zipCode: addressToEdit.zipCode || '',
        country: addressToEdit.country || 'USA'
      });
    } else {
      const availableTypes = getAvailableAddressTypes();
      setFormData({
        addressType: availableTypes.length > 0 ? availableTypes[0] : '',
        streetAddress: '',
        streetAddress2: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA'
      });
    }
    setErrors({});
  }, [addressToEdit, isOpen, getAvailableAddressTypes]);

  const handleInputChange = (field, value) => {
    let processedValue = value;
    if (field === 'zipCode') {
      processedValue = value.slice(0, 5);
    }
    setFormData(prev => ({ ...prev, [field]: processedValue }));

    if (field === 'streetAddress2') {
      if (value && value.length > 255) {
        setErrors(prev => ({ ...prev, [field]: 'Street Address 2 cannot exceed 255 characters' }));
      } else {
        setErrors(prev => {
          const n = { ...prev };
          delete n[field];
          return n;
        });
      }
    } else if (errors[field]) {
      setErrors(prev => {
        const n = { ...prev };
        delete n[field];
        return n;
      });
    }
  };

  const handleAddressSelect = (item) => {
    const label = item?.label || '';
    const details = item?.details || {};
    setFormData(prev => ({
      ...prev,
      streetAddress: label,
      city: details.city || '',
      state: details.state || '',
      zipCode: (details.zipCode || '').slice(0, 5),
      country: details.country || 'USA'
    }));
    setErrors(prev => {
      const n = { ...prev };
      delete n.streetAddress;
      delete n.city;
      delete n.state;
      delete n.zipCode;
      return n;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.addressType) newErrors.addressType = 'Address type is required';
    if (!formData.streetAddress || formData.streetAddress.trim() === '') newErrors.streetAddress = 'Street address is required';
    if (formData.streetAddress2 && formData.streetAddress2.length > 255) newErrors.streetAddress2 = 'Street Address 2 cannot exceed 255 characters';
    if (!formData.city || formData.city.trim() === '') newErrors.city = 'City is required';
    if (!formData.state || formData.state.trim() === '') newErrors.state = 'State is required';
    if (!formData.zipCode || formData.zipCode.trim() === '') newErrors.zipCode = 'Zip code is required';
    else if (formData.zipCode.length > 5) newErrors.zipCode = 'Zip code cannot exceed 5 characters';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) onSave(formData);
  };

  const availableTypes = getAvailableAddressTypes();

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <h5 className="mb-0">
          <i className="ni ni-pin-3 mr-2" />
          {addressToEdit ? 'Edit Address' : 'Add Address'}
        </h5>
      </ModalHeader>
      <ModalBody>
        <Form>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label htmlFor="addressType">Address Type</Label>
                <Input
                  id="addressType"
                  type="select"
                  value={formData.addressType}
                  onChange={(e) => handleInputChange('addressType', e.target.value)}
                  className={errors.addressType ? 'is-invalid' : ''}
                >
                  <option value="">Select Address Type</option>
                  {availableTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Input>
                {errors.addressType && <div className="invalid-feedback d-block">{errors.addressType}</div>}
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label htmlFor="streetAddress">Street Address</Label>
            <ExtAddressAutocomplete
              value={formData.streetAddress}
              onChange={(text) => handleInputChange('streetAddress', text)}
              onSelect={handleAddressSelect}
              placeholder="Start typing address..."
              className={errors.streetAddress ? 'is-invalid' : ''}
            />
            {errors.streetAddress && <div className="invalid-feedback d-block">{errors.streetAddress}</div>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="streetAddress2">Street Address 2 (Optional)</Label>
            <Input
              id="streetAddress2"
              type="text"
              value={formData.streetAddress2}
              onChange={(e) => handleInputChange('streetAddress2', e.target.value)}
              placeholder="Apartment, suite, unit, etc."
              className={errors.streetAddress2 ? 'is-invalid' : ''}
              maxLength={255}
            />
            {errors.streetAddress2 && <div className="invalid-feedback d-block">{errors.streetAddress2}</div>}
          </FormGroup>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className={errors.city ? 'is-invalid' : ''}
                />
                {errors.city && <div className="invalid-feedback d-block">{errors.city}</div>}
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className={errors.state ? 'is-invalid' : ''}
                />
                {errors.state && <div className="invalid-feedback d-block">{errors.state}</div>}
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label htmlFor="zipCode">Zip Code</Label>
                <Input
                  id="zipCode"
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  className={errors.zipCode ? 'is-invalid' : ''}
                  maxLength={5}
                />
                {errors.zipCode && <div className="invalid-feedback d-block">{errors.zipCode}</div>}
              </FormGroup>
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          <i className="ni ni-fat-remove mr-2" />
          Cancel
        </Button>
        <Button color="primary" onClick={handleSave}>
          <i className="ni ni-check-bold mr-2" />
          {addressToEdit ? 'Update' : 'Add'} Address
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddressModal;
